import React from "react";
import { StyleSheet, View } from "react-native";
import { WarningIcon } from "@phosphor-icons/react";
import type { GetModernizedCourseEvaluatorIssuesQuery } from "@axio/graphql/generated/graphql";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import { DIMENSION_ID_ALIASES, type GapAnalysisDimension } from "./gapAnalysisConstants";
import { parseGroupedFindings } from "../utils/gapAnalysisSummary";

interface DimensionIssuePanelProps {
  dimension: GapAnalysisDimension;
  evaluatorIssues?: Array<
    NonNullable<NonNullable<GetModernizedCourseEvaluatorIssuesQuery["getModernizedCourse"]>["evaluatorIssues"]>[number]
  >;
}

function normalizeIssueDimensionId(dimensionId: string): string {
  const normalized = dimensionId
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");
  return DIMENSION_ID_ALIASES[normalized] ?? normalized;
}

function resolveDimensionIssues(
  dimension: GapAnalysisDimension,
  evaluatorIssues: NonNullable<
    NonNullable<GetModernizedCourseEvaluatorIssuesQuery["getModernizedCourse"]>["evaluatorIssues"]
  >
): GapAnalysisDimension {
  const allDimensionIssues = evaluatorIssues.filter(
    (issue) => normalizeIssueDimensionId(issue.dimension) === dimension.id
  );
  const filteredIssues = allDimensionIssues.slice(0, 4);

  const mappedDescriptions = filteredIssues
    .map((issue) => issue.description)
    .filter((description) => description.trim().length > 0);

  const issuesWithEntity = filteredIssues
    .filter((issue) => issue.description.trim().length > 0)
    .map((issue) => ({
      id: issue.id,
      description: issue.description,
      entityId: issue.entityId,
      entityType: issue.entityType,
      title: issue.title,
    }));

  return {
    ...dimension,
    issues: filteredIssues.length,
    issuesList: mappedDescriptions,
    issuesWithEntity,
  };
}

export function DimensionIssuePanel({ dimension, evaluatorIssues = [] }: DimensionIssuePanelProps) {
  const resolvedDimension = resolveDimensionIssues(dimension, evaluatorIssues);

  if (dimension.id === "rubric-quality") {
    return <RubricQualityIssuePanel dimension={resolvedDimension} />;
  }

  if (dimension.id === "clo-coverage" || dimension.id === "industry-alignment") {
    return <GenericIssuePanel dimension={resolvedDimension} />;
  }

  if (dimension.id === "accessibility") {
    return <GenericIssuePanel dimension={resolvedDimension} headerContent={<AccessibilityScopeNotice />} />;
  }

  return <GenericIssuePanel dimension={resolvedDimension} />;
}

// ──────────────────────────────────────────────────────────────────────────────
// Generic issue panel — handles summary, findings, issue list, and optional approval
// ──────────────────────────────────────────────────────────────────────────────

function GenericIssuePanel({
  dimension,
  headerContent,
}: {
  dimension: GapAnalysisDimension;
  headerContent?: React.ReactNode;
}) {
  const { high: highFindings } = parseGroupedFindings(dimension.detailedFindings);
  const shouldShowSummary = Boolean(dimension.summary) && highFindings.length === 0;

  return (
    <View style={styles.issuePanel}>
      {headerContent}
      {shouldShowSummary && <NarrativeSummary summary={dimension.summary!} />}
      {highFindings.length > 0 ? <HighPriorityFindings findings={highFindings} /> : <IssueList dimension={dimension} />}
    </View>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Rubric quality panel — separate layout/styles
// ──────────────────────────────────────────────────────────────────────────────

function RubricQualityIssuePanel({ dimension }: { dimension: GapAnalysisDimension }) {
  const { high: highFindings } = parseGroupedFindings(dimension.detailedFindings);
  const shouldShowSummary = Boolean(dimension.summary) && highFindings.length === 0;
  const rubricIssues =
    dimension.issuesWithEntity && dimension.issuesWithEntity.length > 0
      ? dimension.issuesWithEntity.map((issue) => issue.description)
      : dimension.issuesList;

  return (
    <View style={styles.rubricPanel}>
      {shouldShowSummary && <NarrativeSummary summary={dimension.summary!} />}
      {highFindings.length > 0 ? (
        <HighPriorityFindings findings={highFindings} />
      ) : (
        <View>
          <CustomText
            text={`${rubricIssues.length} ${rubricIssues.length === 1 ? "item" : "items"}`}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.rubricSectionTitle}
          />
          <View style={styles.rubricIssueList}>
            {rubricIssues.map((issue) => (
              <View key={issue} style={styles.rubricIssueItem}>
                <CustomText
                  text={issue}
                  useTranslationText={false}
                  size="s"
                  weight="normal"
                  style={styles.rubricIssueText}
                />
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ──────────────────────────────────────────────────────────────────────────────

function AccessibilityScopeNotice() {
  return (
    <View style={styles.accessibilityScopeNotice}>
      <View style={styles.accessibilityScopeIcon}>
        <WarningIcon size={16} color={colors.warning[700]} weight="bold" />
      </View>
      <View style={styles.accessibilityScopeTextGroup}>
        <CustomText
          text="Limited scope - manual review still required."
          useTranslationText={false}
          size="xs"
          weight="bold"
          style={styles.accessibilityScopeTitle}
        />
        <CustomText
          text="Axio only checks activity titles and descriptions for HTML accessibility signals. Images, videos, slides, PDFs, and external multimedia are not evaluated - verify those manually before publishing."
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.accessibilityScopeText}
        />
      </View>
    </View>
  );
}

function IssueList({ dimension }: { dimension: GapAnalysisDimension }) {
  return (
    <>
      <CustomText
        text={`${dimension.issues} ${dimension.issues === 1 ? "item" : "items"}`}
        useTranslationText={false}
        size="xs"
        weight="bold"
        style={styles.issueCount}
      />
      <View style={styles.issueList}>
        {dimension.issuesWithEntity && dimension.issuesWithEntity.length > 0
          ? dimension.issuesWithEntity.map((issue) => (
              <View key={issue.id} style={styles.issueItem}>
                <CustomText
                  text={issue.description}
                  useTranslationText={false}
                  size="s"
                  weight="normal"
                  style={styles.issueText}
                />
              </View>
            ))
          : dimension.issuesList.map((issue) => (
              <View key={issue} style={styles.issueItem}>
                <CustomText text={issue} useTranslationText={false} size="s" weight="normal" style={styles.issueText} />
              </View>
            ))}
      </View>
    </>
  );
}

function NarrativeSummary({ summary }: { summary: string }) {
  return (
    <View style={styles.narrativeSummary}>
      <CustomText
        text={summary}
        useTranslationText={false}
        size="s"
        weight="normal"
        style={styles.narrativeSummaryText}
      />
    </View>
  );
}

function HighPriorityFindings({ findings }: { findings: string[] }) {
  return (
    <View style={styles.detailedFindingsSection}>
      <CustomText text="Priority items" useTranslationText={false} size="xs" weight="bold" style={styles.issueCount} />
      <View style={styles.findingList}>
        {findings.map((finding) => (
          <View key={finding} style={[styles.findingItem, { borderLeftColor: colors.error[200] }]}>
            <View style={styles.findingBullet} />
            <CustomText
              text={finding}
              useTranslationText={false}
              size="s"
              weight="normal"
              style={styles.findingDescription}
            />
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  accessibilityScopeNotice: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.warning[300],
    backgroundColor: colors.warning[25],
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  accessibilityScopeIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  accessibilityScopeTextGroup: {
    flex: 1,
    gap: 4,
  },
  accessibilityScopeTitle: {
    color: colors.grey[900],
    lineHeight: 18,
  },
  accessibilityScopeText: {
    color: colors.grey[700],
    lineHeight: 18,
  },
  issuePanel: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    backgroundColor: colors.grey[25],
    paddingHorizontal: 24,
    paddingVertical: 22,
  },
  issueCount: {
    color: colors.grey[500],
    textTransform: "uppercase",
    marginBottom: 16,
  },
  issueList: {
    gap: 14,
  },
  issueItem: {
    borderLeftWidth: 2,
    borderLeftColor: colors.grey[200],
    paddingLeft: 16,
    gap: 0,
  },
  issueText: {
    color: colors.grey[900],
    lineHeight: 22,
  },
  rubricPanel: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    backgroundColor: colors.grey[25],
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  rubricSectionTitle: {
    color: colors.grey[500],
    fontSize: 11,
    lineHeight: 16,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 12,
  },
  rubricIssueList: {
    gap: 10,
  },
  rubricIssueItem: {
    borderLeftWidth: 2,
    borderLeftColor: colors.grey[200],
    paddingLeft: 12,
    overflow: "hidden",
  },
  rubricIssueText: {
    color: colors.grey[900],
    lineHeight: 21,
  },
  narrativeSummary: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 6,
    backgroundColor: colors.grey[50],
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  narrativeSummaryText: {
    color: colors.grey[800],
    lineHeight: 22,
  },
  detailedFindingsSection: {
    gap: 16,
  },
  findingList: {
    gap: 10,
  },
  findingItem: {
    borderLeftWidth: 2,
    paddingLeft: 12,
    gap: 8,
    flexDirection: "row",
    alignItems: "flex-start",
    borderRadius: 4,
    paddingVertical: 2,
  },
  findingBullet: {
    width: 5,
    height: 5,
    borderRadius: 99,
    backgroundColor: colors.error[500],
    marginTop: 8,
    flexShrink: 0,
  },
  findingDescription: {
    color: colors.grey[800],
    lineHeight: 22,
  },
});
